import JsBarcode from "jsbarcode";

/**
 * Barcode symbologies supported by the generator.
 *
 * Each value maps directly to a JsBarcode `format` string, so the set is a
 * deliberate subset of JsBarcode's formats covering the ones useful for
 * warehouse/scanning testing.
 */
export type BarcodeFormat =
  | "CODE128"
  | "CODE39"
  | "EAN13"
  | "EAN8"
  | "UPC"
  | "ITF14"
  | "MSI"
  | "pharmacode"
  | "codabar";

/**
 * Full configuration for a bulk barcode render.
 *
 * The shape is intentionally flat and JSON-serializable so it can round-trip
 * through both `localStorage` and shareable URL query parameters without any
 * transformation.
 */
export type BarcodeSettings = {
  /** Raw multiline text; one barcode value per non-empty line. */
  values: string;
  /** JsBarcode symbology to render. */
  format: BarcodeFormat;
  /** Width in pixels of a single narrow bar (JsBarcode `width`). */
  width: number;
  /** Bar height in pixels (JsBarcode `height`). */
  height: number;
  /** Font size in pixels for the human-readable text. */
  fontSize: number;
  /** Font family for the human-readable text. */
  font: string;
  /** Whether to render the human-readable value under the bars. */
  displayValue: boolean;
  /** Number of columns in the preview/print grid. */
  columns: number;
  /** Quiet-zone margin in pixels around each barcode (JsBarcode `margin`). */
  margin: number;
  /** Bar color as a CSS hex string. */
  lineColor: string;
  /** Background color as a CSS hex string. */
  background: string;
};

/**
 * The subset of {@link BarcodeSettings} controlling how a single barcode
 * renders. Field names map 1:1 to JsBarcode options, so a value of this type
 * can be passed straight to `JsBarcode()`.
 */
export type BarcodeRenderOptions = Pick<
  BarcodeSettings,
  | "format"
  | "width"
  | "height"
  | "fontSize"
  | "font"
  | "displayValue"
  | "margin"
  | "lineColor"
  | "background"
>;

/**
 * Renders a barcode to an off-screen canvas and triggers a PNG download named
 * after the value.
 *
 * @param value - The barcode value to encode.
 * @param options - JsBarcode render options.
 * @throws When JsBarcode cannot encode `value` for the chosen symbology.
 */
export const downloadBarcodePng = (
  value: string,
  options: BarcodeRenderOptions,
): void => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, options);
  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = `${value}.png`;
  link.click();
};

/** Selectable symbologies with human-readable labels for the format dropdown. */
export const BARCODE_FORMAT_OPTIONS: ReadonlyArray<{
  value: BarcodeFormat;
  label: string;
}> = [
  { value: "CODE128", label: "Code 128 (default)" },
  { value: "CODE39", label: "Code 39" },
  { value: "EAN13", label: "EAN-13" },
  { value: "EAN8", label: "EAN-8" },
  { value: "UPC", label: "UPC-A" },
  { value: "ITF14", label: "ITF-14" },
  { value: "MSI", label: "MSI" },
  { value: "pharmacode", label: "Pharmacode" },
  { value: "codabar", label: "Codabar" },
];

/** Font families offered for the human-readable text. */
export const FONT_OPTIONS: readonly string[] = [
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "monospace",
];

/**
 * Default settings, matching the sensible defaults of the free-barcode.com
 * bulk generator (Code 128, readable text on, modest bar width/height).
 */
export const DEFAULT_SETTINGS: BarcodeSettings = {
  values: "",
  format: "CODE128",
  width: 2,
  height: 100,
  fontSize: 20,
  font: "Arial",
  displayValue: true,
  columns: 3,
  margin: 10,
  lineColor: "#000000",
  background: "#ffffff",
};

/** `localStorage` key under which the last-used settings are persisted. */
export const STORAGE_KEY = "internal-tools.barcode-generator.settings";

/** Bounds applied to numeric settings to keep renders and URLs sane. */
const NUMERIC_BOUNDS = {
  width: { min: 1, max: 10 },
  height: { min: 10, max: 400 },
  fontSize: { min: 8, max: 60 },
  columns: { min: 1, max: 8 },
  margin: { min: 0, max: 50 },
} as const;

/**
 * Splits the raw multiline `values` string into individual barcode values,
 * trimming whitespace and dropping empty lines.
 *
 * @param values - Raw multiline textarea content.
 * @returns The ordered list of non-empty barcode values.
 */
export const parseValues = (values: string): string[] =>
  values
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/** Inclusive bounds for a serialized-value batch. */
export const SERIAL_BOUNDS = { count: { min: 1, max: 1000 } } as const;

/**
 * Generates a sequential batch of barcode values from a base value.
 *
 * Appends a zero-padded, `+`-separated counter to `base` (e.g. `base+001`,
 * `base+002`). Padding width is the larger of 3 and the digit count of the
 * highest number, so all values in the batch share the same width.
 *
 * @param base - The base value to serialize; trimmed before use.
 * @param count - How many sequential values to generate (clamped to
 *   {@link SERIAL_BOUNDS}).
 * @param start - The first sequence number (defaults to 1).
 * @returns The ordered list of serialized values; empty when `base` is blank.
 */
export const generateSerializedValues = (
  base: string,
  count: number,
  start = 1,
): string[] => {
  const trimmedBase = base.trim();
  if (trimmedBase.length === 0) {
    return [];
  }
  const safeCount = clamp(
    Math.trunc(count),
    SERIAL_BOUNDS.count.min,
    SERIAL_BOUNDS.count.max,
  );
  const end = start + safeCount - 1;
  const padWidth = Math.max(3, String(end).length);
  return Array.from({ length: safeCount }, (_, index) => {
    const sequence = String(start + index).padStart(padWidth, "0");
    return `${trimmedBase}+${sequence}`;
  });
};

/**
 * Clamps a number to the inclusive `[min, max]` range.
 *
 * @param value - The value to clamp.
 * @param min - Lower bound.
 * @param max - Upper bound.
 * @returns The clamped value.
 */
const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Parses a query-string value into a bounded integer, falling back to a
 * default when the input is missing or not a finite number.
 *
 * @param raw - The raw query-parameter string (or null when absent).
 * @param fallback - Value to use when parsing fails.
 * @param bounds - Inclusive min/max applied to the parsed number.
 * @returns The clamped integer, or `fallback` when `raw` is unusable.
 */
const parseBoundedInt = (
  raw: string | null,
  fallback: number,
  bounds: { min: number; max: number },
): number => {
  if (raw === null) {
    return fallback;
  }
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  return clamp(parsed, bounds.min, bounds.max);
};

/**
 * Serializes settings into URL query parameters for link sharing.
 *
 * Values are newline-joined; every other field maps to a short, readable
 * parameter so the resulting link is human-inspectable.
 *
 * @param settings - The settings to encode.
 * @returns A `URLSearchParams` instance representing the settings.
 */
export const settingsToSearchParams = (
  settings: BarcodeSettings,
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("values", parseValues(settings.values).join("\n"));
  params.set("format", settings.format);
  params.set("width", String(settings.width));
  params.set("height", String(settings.height));
  params.set("fontSize", String(settings.fontSize));
  params.set("font", settings.font);
  params.set("displayValue", settings.displayValue ? "1" : "0");
  params.set("columns", String(settings.columns));
  params.set("margin", String(settings.margin));
  params.set("lineColor", settings.lineColor);
  params.set("background", settings.background);
  return params;
};

/**
 * Type guard confirming a string is a supported {@link BarcodeFormat}.
 *
 * @param value - Candidate format string.
 * @returns `true` when `value` is a supported symbology.
 */
const isBarcodeFormat = (value: string): value is BarcodeFormat =>
  BARCODE_FORMAT_OPTIONS.some((option) => option.value === value);

/**
 * Reconstructs settings from URL query parameters, applying defaults and
 * numeric bounds for any missing or invalid field.
 *
 * @param search - The `location.search` string (with or without leading `?`).
 * @returns Fully-populated settings derived from the query string.
 */
export const settingsFromSearchParams = (search: string): BarcodeSettings => {
  const params = new URLSearchParams(search);
  const format = params.get("format");
  const rawDisplayValue = params.get("displayValue");
  return {
    values: params.get("values") ?? DEFAULT_SETTINGS.values,
    format:
      format !== null && isBarcodeFormat(format)
        ? format
        : DEFAULT_SETTINGS.format,
    width: parseBoundedInt(
      params.get("width"),
      DEFAULT_SETTINGS.width,
      NUMERIC_BOUNDS.width,
    ),
    height: parseBoundedInt(
      params.get("height"),
      DEFAULT_SETTINGS.height,
      NUMERIC_BOUNDS.height,
    ),
    fontSize: parseBoundedInt(
      params.get("fontSize"),
      DEFAULT_SETTINGS.fontSize,
      NUMERIC_BOUNDS.fontSize,
    ),
    font: params.get("font") ?? DEFAULT_SETTINGS.font,
    displayValue:
      rawDisplayValue === null
        ? DEFAULT_SETTINGS.displayValue
        : rawDisplayValue === "1",
    columns: parseBoundedInt(
      params.get("columns"),
      DEFAULT_SETTINGS.columns,
      NUMERIC_BOUNDS.columns,
    ),
    margin: parseBoundedInt(
      params.get("margin"),
      DEFAULT_SETTINGS.margin,
      NUMERIC_BOUNDS.margin,
    ),
    lineColor: params.get("lineColor") ?? DEFAULT_SETTINGS.lineColor,
    background: params.get("background") ?? DEFAULT_SETTINGS.background,
  };
};

/**
 * Resolves the initial settings for the tool.
 *
 * Precedence: URL query parameters (a shared link) win; otherwise the last
 * persisted `localStorage` settings; otherwise {@link DEFAULT_SETTINGS}.
 *
 * @returns The settings to initialize the generator with.
 */
export const loadInitialSettings = (): BarcodeSettings => {
  if (globalThis.location.search.length > 1) {
    return settingsFromSearchParams(globalThis.location.search);
  }
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_KEY);
    if (stored !== null && stored.length > 0) {
      return {
        ...DEFAULT_SETTINGS,
        ...(JSON.parse(stored) as BarcodeSettings),
      };
    }
  } catch {
    // Corrupt or unavailable storage: fall through to defaults.
  }
  return DEFAULT_SETTINGS;
};

/**
 * Persists settings to `localStorage`, ignoring storage failures (private
 * mode, quota) since persistence is a convenience, not a requirement.
 *
 * @param settings - The settings to persist.
 */
export const persistSettings = (settings: BarcodeSettings): void => {
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Persistence is best-effort; ignore storage errors.
  }
};

/**
 * Builds an absolute, shareable URL that reproduces the given settings when
 * opened.
 *
 * @param settings - The settings to encode into the link.
 * @returns An absolute URL string pointing at the current page with encoded
 *   settings in the query string.
 */
export const buildShareUrl = (settings: BarcodeSettings): string => {
  const params = settingsToSearchParams(settings);
  return `${globalThis.location.origin}${globalThis.location.pathname}?${params.toString()}`;
};
